import * as breakpoint from '@pixelunion/breakpoint';
import EventHandler from '@pixelunion/events';
import Flickity from 'flickity';
import loadYoutubeApi from '../utils/loadYoutubeApi';
import ProductZoom from './ProductZoom';

export default class ProductGallery {
  constructor(el, settings = {}) {
    this.el = el;
    this.settings = settings;
    this.layout = this.el.dataset.productGalleryLayout;
    this.viewport = this.el.querySelector('[data-product-gallery-viewport]');
    this.navigation = this.el.querySelector('[data-product-gallery-navigation]');

    this.figures = this.viewport.querySelectorAll('[data-product-gallery-figure]');
    this.selected = {
      figure: this.viewport.querySelector('[data-product-gallery-selected="true"]'),
      thumbnail: null,
    };
    this.media = {
      images: [],
      models: [],
      videos: [],
    };

    if (this.layout === 'list') {
      const imageEls = this.viewport.querySelectorAll('[data-media-type="image"][data-product-gallery-image-zoom]');
      for (let i = 0; i < imageEls.length; i++) {
        this._initZoom(imageEls[i]);
      }
    } else if (this.selected.figure.dataset.mediaType === 'image') {
      this._initZoom(this.selected.figure);
    }

    this.events = new EventHandler();

    if (this.navigation) {
      this.selected.thumbnail = this.navigation.querySelector('[data-product-gallery-selected="true"]');
      this.navigationLayout = this.navigation.dataset.productGalleryNavigationLayout;
      this.navigationScroller = this.navigation.querySelector('[data-product-gallery-navigation-scroller]');
      this.navigationScrollUpButton = this.navigation.querySelector('[data-product-gallery-navigation-scroll-up]');
      this.navigationScrollDownButton = this.navigation.querySelector('[data-product-gallery-navigation-scroll-down]');
      this.navigationPreviousButton = this.navigation.querySelector('[data-product-gallery-navigation-previous]');
      this.navigationNextButton = this.navigation.querySelector('[data-product-gallery-navigation-next]');
      this.thumbnails = this.el.querySelectorAll('[data-product-gallery-thumbnail]');

      this.onThumbnailClick = e => {
        this._selectMediaByIndex(e.currentTarget.dataset.productGalleryThumbnail, true);
      };

      this.thumbnails.forEach(thumbnail => this.events.register(thumbnail, 'click', this.onThumbnailClick));

      this._initScroller();
      this._initPagination();
    }

    const features = [];

    if (this.el.querySelector('[data-media-type="model"]')) {
      this.events.register(this.viewport, 'click', e => {
        if ('shopifyXr' in e.target.dataset) {
          this._onViewInYourSpaceClick(e.target);
        }
      });

      features.push(
        {
          name: 'model-viewer-ui',
          version: '1.0',
          onLoad: this._onModelLibraryLoad.bind(this),
        },
      );
      features.push(
        {
          name: 'shopify-xr',
          version: '1.0',
        },
      );
    }

    if (this.el.querySelector('[data-media-type="video"]')) {
      Promise.all([
        new Promise(resolve => {
          if (!document.querySelector('#plyr-stylesheet')) {
            const link = document.createElement('link');

            link.setAttribute('id', 'plyr-stylesheet');
            link.setAttribute('rel', 'stylesheet');
            link.setAttribute('href', 'https://cdn.shopify.com/shopifycloud/shopify-plyr/v1.0/shopify-plyr.css');
            link.onload = resolve;
            document.body.appendChild(link);
          } else {
            resolve();
          }
        }),
        new Promise(resolve => {
          features.push({
            name: 'video-ui',
            version: '1.0',
            onLoad: resolve,
          });
        }),
      ]).then(this._onVideoLibraryLoad.bind(this));
    }

    if (this.el.querySelector('[data-media-type="external_video"]')) {
      loadYoutubeApi().then(this._onExternalVideoLibraryLoad.bind(this));
    }

    if (features.length) {
      Shopify.loadFeatures(features);
    }

    this._onBreakpointChange = this._onBreakpointChange.bind(this);
    breakpoint.onChange(this._onBreakpointChange);

    this._onBreakpointChange();

    this.events.register(this.el, 'rimg:load', () => {
      if (this.flickity) {
        this.flickity.resize();
      }

      this._updateNavigationHeight();
    });
  }

  selectMediaByVariant(variant) {
    if (!variant.featured_media) return;

    const figure = this.viewport.querySelector(`[data-media="${variant.featured_media.id}"]`);
    this._selectMediaByIndex(figure.dataset.productGalleryFigure);
  }

  pauseVideos(excludeVideo = null) {
    this.media.videos.forEach(v => {
      if (v !== excludeVideo) {
        v.pause();
      }
    });
  }

  pauseModels(excludeModel = null) {
    this.media.models.forEach(m => {
      if (m !== excludeModel && m.ui.interacting) {
        m.ui.pause();
      }
    });
  }

  unload() {
    this._destroyScroller();
    this._destroyPagination();
    this.events.unregisterAll();
    this.media.videos.forEach(v => v.unload());
    this.media.images.forEach(i => i.unload());
    if (this.flickity) {
      this.flickity.destroy();
    }
    if (this.navigation) {
      this.navigation.style.height = '';

      if (this.navigationScroller) {
        this.navigationScroller.style.maxHeight = '';
      }
    }
    breakpoint.offChange(this._onBreakpointChange);
  }

  _onModelLibraryLoad() {
    const onPlayListener = () => {
      if (this.flickity) {
        this.flickity.unbindDrag();
      }
    };
    const onPauseListener = () => {
      if (this.flickity) {
        this.flickity.bindDrag();
      }
    };

    const controls = ['zoom-in', 'zoom-out'];
    if (document.fullscreenEnabled) controls.push('fullscreen');

    this.viewport.querySelectorAll('[data-media-type="model"]')
      .forEach(modelFigure => {
        const modelEl = modelFigure.querySelector('model-viewer');
        this.media.models.push({
          ui: new Shopify.ModelViewerUI(modelEl, { controls }),
          el: modelEl,
          figure: modelFigure,
        });

        this.events.register(modelEl, 'shopify_model_viewer_ui_toggle_play', onPlayListener);
        this.events.register(modelEl, 'shopify_model_viewer_ui_toggle_pause', onPauseListener);

        if (this.selected.figure.contains(modelEl)) {
          if (this.flickity) {
            this.flickity.resize();
          }

          this._updateNavigationHeight();
        }
      });
  }

  _onViewInYourSpaceClick(target) {
    if (target.dataset.shopifyModel3dId === this.selected.figure.dataset.media) return;

    const figure = this.viewport.querySelector(`[data-media="${target.dataset.shopifyModel3dId}"]`);
    this._selectMediaByEl(figure);
  }

  _selectMediaByEl(el) {
    this._selectMediaByIndex(parseInt(el.dataset.productGalleryFigure, 10));
  }

  _onVideoLibraryLoad() {
    const videoFigures = this.viewport.querySelectorAll('[data-media-type="video"]');

    for (let i = 0; i < videoFigures.length; i++) {
      const videoFigure = videoFigures[i];
      const videoEl = videoFigure.querySelector('video');
      const player = new Shopify.Plyr(
        videoEl,
        {
          loop: { active: this.settings.enable_video_looping },
        },
      );
      const video = {
        figure: videoFigure,
        el: videoEl,
        player,
        restart: () => {
          player.restart();
          player.play();
        },
        pause: () => player.pause(),
        play: () => player.play(),
        unload: () => player.destroy(),
      };

      this.events.register(videoFigure, 'play', () => this.pauseVideos(video));
      this.media.videos.push(video);

      if (this.selected.figure.contains(videoFigure)) {
        if (this.flickity) {
          this.flickity.resize();
        }

        this._updateNavigationHeight();
      }
    }
  }

  _onExternalVideoLibraryLoad() {
    const videoFigures = this.viewport.querySelectorAll('[data-media-type="external_video"]');

    for (let i = 0; i < videoFigures.length; i++) {
      const videoFigure = videoFigures[i];
      const videoEl = videoFigure.querySelector('iframe');
      const player = new YT.Player(videoEl);
      const video = {
        figure: videoFigure,
        el: videoEl,
        player,
        restart: () => {
          player.seekTo(0);
          player.playVideo();
        },
        pause: () => player.pauseVideo(),
        play: () => player.playVideo(),
        unload: () => {},
      };

      this.events.register(player, 'onStateChange', ({ data }) => {
        if (data === YT.PlayerState.ENDED && this.settings.enable_video_looping) {
          video.restart();
        } else if (data === YT.PlayerState.PLAYING) {
          this.pauseVideos(video);
        }
      });
      this.media.videos.push(video);

      if (this.flickity) {
        this.flickity.resize();
      }

      this._updateNavigationHeight();
    }
  }

  _onBreakpointChange() {
    // If the resize was trigger by a fullscreen, do not do anything
    if (document.fullscreenElement) {
      return;
    }

    if (this.flickity && breakpoint.min('L')) {
      this._destroyFlickity();
    } else if (!this.flickity && breakpoint.max('M')) {
      this._initFlickity();
    }

    this._updateNavigationHeight();
  }

  _initPagination() {
    if (!this.navigationPreviousButton || !this.navigationNextButton) return;

    this.navigatePrevious = () => {
      const index = (((parseInt(this.selected.figure.dataset.productGalleryFigure, 10) - 1) % this.figures.length) + this.figures.length) % this.figures.length;

      this._selectMediaByIndex(index);
    };
    this.navigateNext = () => {
      const index = (parseInt(this.selected.figure.dataset.productGalleryFigure, 10) + 1) % this.figures.length;

      this._selectMediaByIndex(index);
    };

    this.navigationPreviousButton.addEventListener('click', this.navigatePrevious);
    this.navigationNextButton.addEventListener('click', this.navigateNext);
  }

  _destroyPagination() {
    if (!this.navigationPreviousButton || !this.navigationNextButton) return;

    this.navigationPreviousButton.addEventListener('click', this.navigatePrevious);
    this.navigationNextButton.addEventListener('click', this.navigateNext);
  }

  _initScroller() {
    if (!this.navigationScroller) return;

    this._scrollerScrollUp = () => this._scrollerScroll('up');
    this._scrollerScrollDown = () => this._scrollerScroll('down');
    this._scrollerScrollScroll = () => this._updateScrollerButtons();

    this.navigationScrollUpButton.addEventListener('click', this._scrollerScrollUp);
    this.navigationScrollDownButton.addEventListener('click', this._scrollerScrollDown);
    this.navigationScroller.addEventListener('scroll', this._scrollerScrollScroll);

    this._updateNavigationHeight();
    this._updateScrollerButtons();
  }

  _destroyScroller() {
    if (!this.navigationScroller) return;

    this.navigationScrollUpButton.removeEventListener('click', this._scrollerScrollUp);
    this.navigationScrollDownButton.removeEventListener('click', this._scrollerScrollDown);
    this.navigationScroller.removeEventListener('scroll', this._scrollerScrollScroll);
  }

  _scrollerScroll(direction) {
    const scrollerOrientation = this.layout === 'gallery-below' || breakpoint.max('M') ? 'horizontal' : 'vertical';

    if (scrollerOrientation === 'horizontal') {
      if (direction === 'up') {
        this._scrollerScrollHorizontal('left');
      } else {
        this._scrollerScrollHorizontal('right');
      }
      this._updateScrollerButtons();
    } else {
      this._scrollerScrollVertical(direction);
      this._updateScrollerButtons();
    }
  }

  _scrollerScrollVertical(direction) {
    const navigationScrollerTop = this.navigationScroller.getBoundingClientRect().top;
    let closestDistance = Infinity;
    let closestIndex = -1;

    for (let i = 0; i < this.thumbnails.length; i++) {
      const thumbnail = this.thumbnails[i];
      const { top, bottom } = thumbnail.getBoundingClientRect();
      const distance = Math.floor(Math.abs(navigationScrollerTop - (direction === 'up' ? bottom : top)));

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = i;
      }
    }

    const $navigationScroller = $(this.navigationScroller);
    const navigateToImage = direction === 'up'
      ? this.thumbnails[Math.max(closestIndex - 1, 0)]
      : this.thumbnails[Math.min(closestIndex + 1, this.thumbnails.length - 1)];
    const navigateToImageDistance = navigateToImage.getBoundingClientRect().top - navigationScrollerTop;

    $navigationScroller.animate({
      scrollTop: this.navigationScroller.scrollTop + navigateToImageDistance,
    }, { queue: false, duration: 500 });
  }

  _scrollerScrollHorizontal(direction) {
    const navigationScrollerLeft = this.navigationScroller.getBoundingClientRect().left;
    let closestDistance = Infinity;
    let closestIndex = -1;

    for (let i = 0; i < this.thumbnails.length; i++) {
      const thumbnail = this.thumbnails[i];
      const { left, right } = thumbnail.getBoundingClientRect();
      const distance = Math.floor(Math.abs(navigationScrollerLeft - (direction === 'left' ? right : left)));

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = i;
      }
    }

    const $navigationScroller = $(this.navigationScroller);
    const navigateToImage = direction === 'left'
      ? this.thumbnails[Math.max(closestIndex - 1, 0)]
      : this.thumbnails[Math.min(closestIndex + 1, this.thumbnails.length - 1)];
    const navigateToImageDistance = navigateToImage.getBoundingClientRect().left - navigationScrollerLeft;

    $navigationScroller.animate({
      scrollLeft: this.navigationScroller.scrollLeft + navigateToImageDistance,
    }, { queue: false, duration: 500 });
  }

  _updateScrollerButtons() {
    const direction = this.layout === 'gallery-below' || breakpoint.max('M') ? 'horizontal' : 'vertical';
    let scrollPosition;
    let totalPixels;
    let visiblePixels;

    if (direction === 'horizontal') {
      const { scrollLeft, scrollWidth, clientWidth } = this.navigationScroller;
      scrollPosition = scrollLeft;
      totalPixels = scrollWidth;
      visiblePixels = clientWidth;
    } else {
      const { scrollTop, scrollHeight, clientHeight } = this.navigationScroller;
      scrollPosition = scrollTop;
      totalPixels = scrollHeight;
      visiblePixels = clientHeight;
    }

    if (scrollPosition === 0) {
      this.navigationScrollUpButton.setAttribute('disabled', true);
      this.navigationScrollDownButton.removeAttribute('disabled');
    } else if ((totalPixels - visiblePixels) === scrollPosition) {
      this.navigationScrollUpButton.removeAttribute('disabled');
      this.navigationScrollDownButton.setAttribute('disabled', true);
    } else {
      this.navigationScrollUpButton.removeAttribute('disabled');
      this.navigationScrollDownButton.removeAttribute('disabled');
    }
  }

  _initFlickity() {
    this.flickity = new Flickity(this.viewport, {
      accessibility: true,
      adaptiveHeight: true,
      cellSelector: '[data-product-gallery-figure]',
      cellAlign: 'left',
      friction: 0.8,
      pageDots: false,
      prevNextButtons: false,
      selectedAttraction: 0.2,
      wrapAround: true,
    });

    // Flickity controls the pointerdown event which prevents model-viewer-ui from
    // triggering .play() on mouseup. This returns this functionality.
    this.flickity.on('staticClick', (event, pointer, cellElement) => {
      // Ignore anything but mouse events, touch events currently are bugged due to model-viewer-ui
      // touchEnd triggers the model to pause after we play here, returning the model to a paused state
      if (event.pointerType !== 'mouse') return;

      this.media.models.forEach(model => {
        if (cellElement.contains(model.el) && !model.ui.interacting) {
          model.ui.play();
        }
      });
    });

    this.flickity.on('change', index => this._onMediaSelect(index));
  }

  _destroyFlickity() {
    this.flickity.destroy();
    this.flickity = null;
  }

  _onVideoSelect(video) {
    if (this.settings.enable_video_autoplay && breakpoint.min('L') && this.layout !== 'list') {
      video.play();
    }
  }

  _onMediaSelect(index, focus = false) {
    const figure = this.figures[index];

    this.selected.figure.dataset.productGallerySelected = false;
    if (this.layout !== 'list') {
      this.selected.figure.setAttribute('aria-hidden', true);
    }
    this.selected.figure = figure;
    this.selected.figure.dataset.productGallerySelected = true;
    if (this.layout !== 'list') {
      this.selected.figure.setAttribute('aria-hidden', false);
    }

    if (this.navigation) {
      const thumbnail = this.thumbnails[index];

      this.selected.thumbnail.dataset.productGallerySelected = false;
      this.selected.thumbnail = thumbnail;
      this.selected.thumbnail.dataset.productGallerySelected = true;
    }

    const viewInYourSpaceEls = this.el.querySelectorAll('[data-shopify-xr]');
    const setViewInYourSpaceID = id => {
      for (let i = 0; i < viewInYourSpaceEls.length; i++) {
        viewInYourSpaceEls[i].dataset.shopifyModel3dId = id;
      }
    };

    if (viewInYourSpaceEls.length) {
      setViewInYourSpaceID(viewInYourSpaceEls[0].dataset.defaultModelId);
    }

    this.pauseVideos();
    this.pauseModels();

    if (focus) {
      this.media.videos.forEach(v => {
        if (v.figure === this.selected.figure) {
          v.el.focus();
        }
      });
      this.media.models.forEach(m => {
        if (m.figure === this.selected.figure) {
          m.ui.play();
          m.el.focus();
        }
      });
    }

    switch (this.selected.figure.dataset.mediaType) {
      case 'video':
      case 'external_video':
        this._onVideoSelect(this.media.videos.filter(v => v.figure === this.selected.figure)[0]);
        break;
      case 'model':
        setViewInYourSpaceID(this.selected.figure.dataset.media);
        break;
      case 'image':
        this._initZoom(this.selected.figure);
        break;
      default:
        break;
    }

    this._updateNavigationHeight();
  }

  _selectMediaByIndex(index, focus = false) {
    if (this.flickity) {
      this.flickity.select(index);
    }

    this._onMediaSelect(index, focus);
  }

  _updateNavigationHeight() {
    if (!this.navigation) return;

    if (this.navigationLayout !== 'thumbnails') {
      this.navigation.style.height = '';

      if (this.navigationScroller) {
        this.navigationScroller.style.maxHeight = '';
      }

      return;
    }

    const direction = this.layout === 'gallery-below' || breakpoint.max('M') ? 'horizontal' : 'vertical';
    const size = direction === 'vertical' ? 'height' : 'width';
    const sizeScroll = direction === 'vertical' ? 'scrollHeight' : 'scrollWidth';

    const available = this.selected.figure.getBoundingClientRect()[size];
    const required = this.navigationScroller[sizeScroll];

    const minimum = 280;

    if (required < available) {
      this.navigationScrollUpButton.setAttribute('hidden', true);
      this.navigationScrollDownButton.setAttribute('hidden', true);
    } else {
      this.navigationScrollUpButton.removeAttribute('hidden');
      this.navigationScrollDownButton.removeAttribute('hidden');
    }

    if (direction === 'vertical') {
      this.navigation.style.height = `${available < minimum ? minimum : available}px`;
      this.navigationScroller.style.maxHeight = '5000px';
    } else {
      this.navigation.style.height = '';
      this.navigationScroller.style.maxHeight = '';
    }
  }

  _initZoom(figure) {
    const exists = this.media.images.filter(image => image.el.dataset.media === figure.dataset.media);

    if (this.media.images.length === 0 || exists.length === 0) {
      this.media.images.push(new ProductZoom(figure));
    } 
  }
}
